import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Container, Route, Eye } from "lucide-react";

/**
 * 3-step "How it works" section — big icons, short titles, big spacing.
 * Client cue: "hazlo como para niños pequeños".
 */

const STEPS = [
  {
    key: "container",
    icon: Container,
    color: "#0B4C7A",
    kicker: "01",
  },
  {
    key: "route",
    icon: Route,
    color: "#10B981",
    kicker: "02",
  },
  {
    key: "transparency",
    icon: Eye,
    color: "#E76F51",
    kicker: "03",
  },
];

export default function ComoFunciona() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const copy = {
    es: {
      section_kicker: "Cómo funciona",
      section_title: "Sencillo: contenedor, recogida y transparencia",
      section_sub:
        "Tres pasos para convertir la basura del Salnés en un dato útil que cualquiera puede consultar.",
      container_title: "Contenedor inteligente",
      container_sub:
        "Cada contenedor tiene un chip RFID. Cuando el camión lo levanta y lo pesa, ese dato viaja al sistema.",
      route_title: "Recogida optimizada",
      route_sub:
        "La flota traza su ruta con GPS. Sabemos qué se recogió, dónde y cuándo — sin papel, sin dudas.",
      transparency_title: "Transparencia pública",
      transparency_sub:
        "Los datos se publican aquí, en cifras y mapas. Sin filtros. Sin adornos. Los ve todo el mundo.",
    },
    gl: {
      section_kicker: "Como funciona",
      section_title: "Sinxelo: contedor, recollida e transparencia",
      section_sub:
        "Tres pasos para converter o lixo do Salnés nun dato útil que calquera pode consultar.",
      container_title: "Contedor intelixente",
      container_sub:
        "Cada contedor ten un chip RFID. Cando o camión o levanta e o pesa, ese dato viaxa ao sistema.",
      route_title: "Recollida optimizada",
      route_sub:
        "A flota traza a súa ruta con GPS. Sabemos que se recolleu, onde e cando — sen papel, sen dúbidas.",
      transparency_title: "Transparencia pública",
      transparency_sub:
        "Os datos publícanse aquí, en cifras e mapas. Sen filtros. Sen adornos. Ve todo o mundo.",
    },
  };
  const L = copy[lang];

  return (
    <section
      id="como-funciona"
      className="bg-salnes-ivory py-24 lg:py-32"
      data-testid="landing-como-funciona"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.55 }}
          className="max-w-3xl mb-14 lg:mb-20"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-4">
            {L.section_kicker}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-salnes-ink">
            {L.section_title}
          </h2>
          <p className="mt-4 text-salnes-stone text-lg max-w-xl leading-relaxed">
            {L.section_sub}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 relative">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{
                  duration: 0.6,
                  delay: idx * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="relative"
                data-testid={`como-funciona-step-${s.key}`}
              >
                <div
                  className="w-28 h-28 lg:w-32 lg:h-32 rounded-3xl flex items-center justify-center mb-8"
                  style={{ background: `${s.color}12`, color: s.color }}
                  aria-hidden
                >
                  <Icon size={56} strokeWidth={1.4} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-stone/60 mb-2">
                  {s.kicker}
                </div>
                <div className="font-serif text-2xl sm:text-3xl text-salnes-ink leading-tight">
                  {L[`${s.key}_title`]}
                </div>
                <p className="mt-4 text-salnes-stone text-base leading-relaxed max-w-sm">
                  {L[`${s.key}_sub`]}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
